/** @file Hook: a panel's key bindings, live only while focus is inside the panel (pushContext on focus). */
import { useEffect, useRef, type FocusEvent } from 'react';
import { pushContext } from '@pose2d/shortcuts/pushContext';
import type { Binding } from '@pose2d/shortcuts/types';
import { registerEffective } from '../shell/shortcuts/registerEffective';

/** Registers `table` (context `context`, user overrides applied) while mounted; spread the returned handlers on the panel root. */
export function usePanelKeys(context: string, table: Binding[], handlers: Record<string, () => void>) {
  const live = useRef(handlers);
  live.current = handlers;
  const pop = useRef<(() => void) | null>(null);
  useEffect(() => {
    const calls = Object.fromEntries(table.map((b) => [b.id, () => { live.current[b.id]?.(); }]));
    const unregister = registerEffective(context, table, calls);
    return () => { unregister(); pop.current?.(); pop.current = null; };
  }, [context, table]);
  return {
    onFocus: (): void => { pop.current ??= pushContext(context); },
    onBlur: (e: FocusEvent<HTMLElement>): void => {
      if (e.currentTarget.contains(e.relatedTarget)) return;
      pop.current?.();
      pop.current = null;
    },
  };
}
